// Section 02 — "How the numbers work". Worked example of the new ERBI accrual
// policy: accruals are received but deliberately ignored; the INVOICE is what
// counts against the budget until the real posting arrives and reconciliation
// swaps them.
//
// Beat order (9 steps: 0–8):
//   0  intro
//   1  Invoice #9955 arrives → Committed 4,000
//   2  SAP posting +6,000 Fenwick → Actual 6,000
//   3  SAP posting +5,500 Deloitte → Actual 11,500
//   4  Quarter-end accrual +4,000 (VSSC) — grayed + "ignored" tag; readout unchanged
//   5  Quarter closes: Q1 Actual 11,500 · Committed 4,000
//   6  Q2: reversal −4,000 — grayed/ignored; readout unchanged
//   7  Q2: real posting +4,000 → Q2 Actual 4,000; invoice still Committed
//   8  Sandra reconciles → Committed 0; total Actual 15,500
//
// The SVG connector + invoice-card slide are retargeted to the REAL posting
// (step 7–8 in Q2). All other mechanics (play/step, reduced-motion, dots,
// running totals) are preserved exactly.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import useReducedMotion from './useReducedMotion';
import { fmtUSD } from './mockData';

const STEPS = [
  `IRS Assistance is budgeted at $18,000 a quarter. Watch one quarter-close flow through it — these are the worked-example numbers from the design docs.`,
  `Invoice #9955 ($4,000) arrives from Deloitte and is approved. SAP hasn't posted it yet — net-60 terms — so ERBI counts it as Committed right away. The money is visible before it clears the ledger.`,
  `Fenwick's invoice posts in SAP: +$6,000 (doc type KR). Actual is the running signed sum of rows like this. Q1 Actual: $6,000 · Committed: $4,000.`,
  `Deloitte posts +$5,500. Q1 Actual: $11,500 · Committed: $4,000 — the #9955 invoice is waiting on SAP.`,
  `Quarter-end. Accounting books a +$4,000 accrual (VSSC reference). ERBI sets accruals aside — they don't count against the budget (accrual rules are pending with the client). The invoice keeps carrying the 4,000. Readout unchanged: Actual $11,500 · Committed $4,000.`,
  `Quarter closes: Q1 reads Actual $11,500 · Committed $4,000 — the expense is visible exactly once, on the invoice.`,
  `New quarter. The accrual reverses: −$4,000 posts to Q2. ERBI ignores it for the same reason — also grayed out. The reversal nets the accrual to zero outside the budget, no readout change.`,
  `Q2: the real invoice posting arrives — +$4,000 (doc KR · May 9 · ref 9955). Q2 Actual: $4,000. Invoice #9955 is now in both places — time to reconcile.`,
  `Sandra reconciles invoice #9955 to the real posting in Reconciliation. Committed drops to $0. Total Actual: $15,500 · Committed: $0 — counted exactly once.`,
];
const LAST = STEPS.length - 1;

// Running totals per step. null → quarter not started yet.
const READOUT = [
  { q1a: 0,     q1c: 0,    q2a: null,  q2c: null },  // 0
  { q1a: 0,     q1c: 4000, q2a: null,  q2c: null },  // 1
  { q1a: 6000,  q1c: 4000, q2a: null,  q2c: null },  // 2
  { q1a: 11500, q1c: 4000, q2a: null,  q2c: null },  // 3
  { q1a: 11500, q1c: 4000, q2a: null,  q2c: null },  // 4 — accrual ignored
  { q1a: 11500, q1c: 4000, q2a: null,  q2c: null },  // 5 — quarter closes
  { q1a: 11500, q1c: 4000, q2a: null,  q2c: null },  // 6 — reversal ignored
  { q1a: 11500, q1c: 4000, q2a: 4000,  q2c: 0, q1cFlag: '⚠ reconcile now' }, // 7
  { q1a: 11500, q1c: 0,    q2a: 4000,  q2c: 0    },  // 8 — reconciled
];

function Card({ on, refEl, extra = '', title, sub, amount, neg, chip, style, ignored }) {
  return (
    <div
      className={`tut-tl-card ${extra}${on ? ' on' : ''}`}
      ref={refEl}
      style={{
        ...(ignored ? { opacity: 0.45, filter: 'saturate(0.15)' } : {}),
        ...style,
      }}
    >
      <div className="txt">
        <div className="t">{title}</div>
        <div className="s">{sub}</div>
      </div>
      <div className={`amt num${neg ? ' neg' : ''}`}>{amount}</div>
      {chip}
    </div>
  );
}

function Tile({ label, value, flag, started }) {
  return (
    <div className={`tut-ro-tile${flag ? ' flagged' : ''}`}>
      <span className="label">{label}</span>
      <span key={`${value}-${flag || ''}`} className="val num">
        {started ? fmtUSD(value) : '—'}
      </span>
      {flag && <span className="flag">{flag}</span>}
    </div>
  );
}

export default function AccrualTimelineSection() {
  const reduced = useReducedMotion();
  const [step, setStep] = useState(() => (reduced ? LAST : 0));
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => {
      setStep((s) => (s >= LAST ? s : s + 1));
    }, reduced ? 1400 : 2600);
    return () => clearInterval(t);
  }, [playing, reduced]);

  useEffect(() => {
    if (step >= LAST) setPlaying(false);
  }, [step]);

  // ── Geometry: invoice-card slide + posting→invoice match line ─────────
  // The slide now targets the real posting in Q2 (step 7–8).
  const stageRef = useRef(null);
  const postingRef = useRef(null);   // Q2 real posting card (link source)
  const comSlotRef = useRef(null);   // Q1 invoice slot (link target)
  const actSlotRef = useRef(null);   // Q2 "reconciled" slot (slide target)
  const [delta, setDelta] = useState({ x: 0, y: 0 });
  const [link, setLink] = useState(null);

  const measure = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const sr = stage.getBoundingClientRect();
    if (comSlotRef.current && actSlotRef.current) {
      const a = comSlotRef.current.getBoundingClientRect();
      const b = actSlotRef.current.getBoundingClientRect();
      setDelta({ x: b.left - a.left, y: b.top - a.top });
    }
    if (postingRef.current && comSlotRef.current) {
      const ar = postingRef.current.getBoundingClientRect();
      const ir = comSlotRef.current.getBoundingClientRect();
      setLink({
        x1: ar.right - sr.left,
        y1: ar.top + ar.height / 2 - sr.top,
        x2: ir.left - sr.left,
        y2: ir.top + ir.height / 2 - sr.top,
      });
    }
  }, []);

  useLayoutEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    if (document.fonts?.ready) document.fonts.ready.then(measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  // Invoice slides from Q1 Committed slot to Q2 Actual slot on reconciliation
  const moved = step >= 8;
  const ro = READOUT[step];

  const goto = (s) => {
    setPlaying(false);
    setStep(Math.max(0, Math.min(LAST, s)));
  };

  return (
    <section id="tut-numbers" className="tut-section">
      <div className="tut-kicker">02 · Committed, Actual, accruals</div>
      <h2>How the numbers work</h2>
      <p className="tut-lead">
        One budget line, two quarters, every kind of row the SAP feed throws at it. ERBI receives
        accruals but sets them aside — the <strong>invoice</strong> is what counts against the
        budget until the real posting arrives and reconciliation swaps them.
      </p>

      <div className="tut-tl">
        <div className="tut-tl-linechip">
          Budget line: <strong>IRS Assistance</strong>
          <span className="bud num">$18,000 / quarter</span>
        </div>

        <div className="tut-tl-stage" ref={stageRef}>
          {/* SVG connector: real posting → invoice, visible while both are present (step 7) */}
          {link && step === 7 && (
            <svg className="tut-tl-svg" aria-hidden="true">
              <path
                d={`M ${link.x1} ${link.y1} C ${link.x1 + 36} ${link.y1}, ${link.x2 - 36} ${link.y2}, ${link.x2} ${link.y2}`}
              />
              <circle cx={link.x1} cy={link.y1} r="4" />
              <circle cx={link.x2} cy={link.y2} r="4" />
            </svg>
          )}

          <div className="tut-tl-quarters">
            {/* ── Q1 ─────────────────────────────────────────────────── */}
            <div className="tut-tl-q">
              <div className="tut-tl-qh">
                Q1 <span>Feb – Apr</span>
              </div>
              <div className="tut-tl-cols">
                <div className="tut-tl-col">
                  <div className="tut-tl-colh">Actual — SAP rows</div>
                  <Card
                    on={step >= 2}
                    title="Fenwick"
                    sub="KR posting · Mar 12"
                    amount="+$6,000"
                  />
                  <Card
                    on={step >= 3}
                    title="Deloitte"
                    sub="KR posting · Apr 2"
                    amount="+$5,500"
                  />
                  {/* Accrual — always shown "on" when visible, but grayed out (ignored) */}
                  <Card
                    on={step >= 4}
                    extra="violet"
                    title="Quarter-end accrual"
                    sub="VSSC · Apr 30 · ref #9955"
                    amount="+$4,000"
                    ignored={step >= 4}
                    chip={
                      step >= 4 ? (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            color: 'oklch(0.54 0.012 255)',
                            background: 'oklch(0.94 0.012 255)',
                            border: '1px solid oklch(0.82 0.01 255)',
                            borderRadius: 6,
                            padding: '1px 6px',
                            whiteSpace: 'nowrap',
                            marginLeft: 'auto',
                          }}
                          title="Accruals don't count against the budget yet — pending client accrual rules"
                        >
                          ignored
                        </span>
                      ) : undefined
                    }
                  />
                  {step >= 4 && (
                    <p className="tut-tl-double-note" style={{ background: 'oklch(0.97 0.005 255)', borderColor: 'oklch(0.88 0.01 255)', color: 'oklch(0.42 0.012 260)' }}>
                      ERBI sets accruals aside — they don't count against the budget (accrual rules are pending with the client). The invoice keeps carrying the $4,000.
                    </p>
                  )}
                </div>
                <div className="tut-tl-col">
                  <div className="tut-tl-colh">Committed — invoices</div>
                  <div className="tut-tl-comslot" ref={comSlotRef}>
                    <div className={`tut-tl-ghost${moved ? ' show' : ''}`}>
                      matched to posting ✓
                    </div>
                    <Card
                      on={step >= 1}
                      extra={`invoice${moved ? ' moved' : ''}${step === 7 ? ' matching' : ''}`}
                      style={{
                        transform: moved
                          ? `translate(${delta.x}px, ${delta.y}px)`
                          : 'translate(0, 0)',
                      }}
                      title="Invoice #9955 — Deloitte"
                      sub={moved ? 'reconciled to real posting by Sandra' : 'approved · sent to AP · unposted'}
                      amount="$4,000"
                      chip={
                        moved ? <span className="tut-chip match">matched</span> : undefined
                      }
                    />
                  </div>
                  {step >= 5 && step < 8 && (
                    <p className="tut-tl-double-note" style={{ marginTop: 8 }}>
                      Q1 closes: Actual $11,500 · Committed $4,000 — the expense is visible exactly once, on the invoice.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* ── Q2 ─────────────────────────────────────────────────── */}
            <div className="tut-tl-q">
              <div className="tut-tl-qh">
                Q2 <span>May – Jul</span>
              </div>
              <div className="tut-tl-cols">
                <div className="tut-tl-col">
                  <div className="tut-tl-colh">Actual — SAP rows</div>
                  {/* Reversal — grayed/ignored */}
                  <Card
                    on={step >= 6}
                    extra="violet"
                    title="Accrual reversal"
                    sub="VSSC · May 1"
                    amount="−$4,000"
                    neg
                    ignored={step >= 6}
                    chip={
                      step >= 6 ? (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            color: 'oklch(0.54 0.012 255)',
                            background: 'oklch(0.94 0.012 255)',
                            border: '1px solid oklch(0.82 0.01 255)',
                            borderRadius: 6,
                            padding: '1px 6px',
                            whiteSpace: 'nowrap',
                            marginLeft: 'auto',
                          }}
                          title="Accruals don't count against the budget yet — pending client accrual rules"
                        >
                          ignored
                        </span>
                      ) : undefined
                    }
                  />
                  {/* Real posting — this is what triggers reconciliation */}
                  <Card
                    on={step >= 7}
                    refEl={postingRef}
                    extra={step === 7 ? 'matching' : ''}
                    title="Invoice #9955 posts"
                    sub="KR posting · May 9 · ref 9955"
                    amount="+$4,000"
                    chip={
                      step >= 8 ? <span className="tut-chip match">matched</span> : undefined
                    }
                  />
                  {/* Slot where the invoice card slides into on reconciliation */}
                  <div
                    className={`tut-tl-slot${step >= 7 ? ' show' : ''}`}
                    ref={actSlotRef}
                  >
                    <span className="slot-label">reconciled invoice lands here</span>
                  </div>
                  {step === 7 && (
                    <p className="tut-tl-double-note">
                      Now it's in both places — invoice in Committed, real posting in Actual. Time to reconcile.
                    </p>
                  )}
                </div>
                <div className="tut-tl-col">
                  <div className="tut-tl-colh">Committed — invoices</div>
                  {step < 7 ? (
                    <div className="tut-tl-empty">nothing committed in Q2</div>
                  ) : (
                    <div style={{ fontSize: 12.5, color: 'oklch(0.5 0.15 260)', fontWeight: 600, padding: '6px 0' }}>
                      #9955 still shows here until Sandra reconciles it
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="tut-tl-readout">
          <Tile label="Q1 Actual" value={ro.q1a} started />
          <Tile label="Q1 Committed" value={ro.q1c} started flag={ro.q1cFlag} />
          <Tile label="Q2 Actual" value={ro.q2a} started={ro.q2a != null} />
          <Tile label="Q2 Committed" value={ro.q2c} started={ro.q2c != null} />
        </div>

        <div className="tut-tl-caption">
          <span className="stepno num">
            {step}/{LAST}
          </span>
          <p>{STEPS[step]}</p>
        </div>

        <div className="tut-tl-controls">
          <button type="button" className="tut-ctl-btn" onClick={() => goto(0)} disabled={step === 0}>
            Restart
          </button>
          <button type="button" className="tut-ctl-btn" onClick={() => goto(step - 1)} disabled={step === 0}>
            Back
          </button>
          <button
            type="button"
            className="tut-ctl-btn primary"
            onClick={() => {
              if (playing) {
                setPlaying(false);
              } else {
                if (step >= LAST) setStep(0);
                setPlaying(true);
              }
            }}
          >
            {playing ? 'Pause' : 'Play'}
          </button>
          <button type="button" className="tut-ctl-btn" onClick={() => goto(step + 1)} disabled={step === LAST}>
            Step
          </button>
          <div className="tut-tl-dots" role="group" aria-label="Timeline steps">
            {STEPS.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to step ${i}`}
                className={`tut-dot${i <= step ? ' done' : ''}${i === step ? ' cur' : ''}`}
                onClick={() => goto(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
